import { CancelIcon } from '@treneo/shared/icons';
import { Chip, Tooltip } from '@treneo/shared/ui';

import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { GridActionsCellItem } from '@mui/x-data-grid';

import { TRAINER_INVITATION_STATUS } from '../data/trainer-invitation-status.constants';

import { getStatusColor, getStatusString } from './invitation-status-utils';

type DateLike = string | Date | null | undefined;

export type FormatDateTimeFn = (date: DateLike, fallback: string) => string;

export type BaseInvitationRow = {
  id: string;
  email: string;
  inviterName: string;
  createdAt: DateLike;
  acceptedAt?: DateLike;
  cancelledAt?: DateLike;
  rejectedAt?: DateLike;
  expiresAt: DateLike;
  status: number;
  courseId?: string | null;
  courseTitle?: string | null;
};

export type SystemInvitationRow = BaseInvitationRow & {
  organizationId: string;
  organizationName: string;
};

export type GetCancelParamsFn = (row: BaseInvitationRow) => {
  organizationId: string;
  invitationId: string;
};

export type GetSystemCancelParamsFn = (row: SystemInvitationRow) => {
  organizationId: string;
  invitationId: string;
};

export type CreateInvitationColumnsParams<T extends BaseInvitationRow> = {
  t: (key: string) => string;
  formatDateTime: FormatDateTimeFn;
  onCancelInvitation?: (organizationId: string, invitationId: string) => void;
  isCancelling?: boolean;
  includeOrganizationColumn?: boolean;
  getCancelParams?: (row: T) => { organizationId: string; invitationId: string };
};

const COLUMN_WIDTHS = {
  organization: 220,
  email: 250,
  inviterName: 200,
  date: 160,
  status: 130,
  inCourseContext: 140,
  course: 200,
  actions: 100
} as const;

const invitationColumnFields = {
  email: 'email',
  inviterName: 'inviterName',
  createdAt: 'createdAt',
  expiresAt: 'expiresAt',
  acceptedAt: 'acceptedAt',
  cancelledAt: 'cancelledAt',
  rejectedAt: 'rejectedAt',
  status: 'status',
  courseId: 'courseId',
  courseTitle: 'courseTitle',
  organizationName: 'organizationName',
  actions: 'actions'
} as const;

type StatusCellProps = Readonly<{
  params: GridRenderCellParams<BaseInvitationRow, number>;
  t: (key: string) => string;
}>;

const InvitationStatusCell = ({ params, t }: StatusCellProps) => {
  const status = params.value ?? 0;
  const statusString = getStatusString(status);
  const statusLabel = t(`trainers.invitationStatus.${statusString}`);
  const color = getStatusColor(status);
  return <Chip label={statusLabel} color={color} size='small' aria-label={statusLabel} />;
};

const createEmailColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string
): GridColDef<T> => ({
  field: invitationColumnFields.email,
  headerName: t('trainers.email'),
  width: COLUMN_WIDTHS.email,
  flex: 1,
  sortable: true,
  filterable: true
});

const createInviterNameColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string
): GridColDef<T> => ({
  field: invitationColumnFields.inviterName,
  headerName: t('trainers.invitedBy'),
  width: COLUMN_WIDTHS.inviterName,
  flex: 1,
  sortable: true,
  filterable: true
});

const createSentAtColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string,
  formatDateTime: FormatDateTimeFn
): GridColDef<T> => ({
  field: invitationColumnFields.createdAt,
  headerName: t('trainers.createdAt'),
  width: COLUMN_WIDTHS.date,
  sortable: true,
  filterable: true,
  valueFormatter: value => formatDateTime(value as DateLike, '')
});

const createExpiresAtColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string,
  formatDateTime: FormatDateTimeFn
): GridColDef<T> => ({
  field: invitationColumnFields.expiresAt,
  headerName: t('trainers.expiresAt'),
  width: COLUMN_WIDTHS.date,
  sortable: true,
  filterable: true,
  valueFormatter: value => formatDateTime(value as DateLike, '')
});

const createAcceptedAtColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string,
  formatDateTime: FormatDateTimeFn
): GridColDef<T> => ({
  field: invitationColumnFields.acceptedAt,
  headerName: t('trainers.acceptedAt'),
  width: COLUMN_WIDTHS.date,
  sortable: true,
  filterable: true,
  valueFormatter: value => formatDateTime(value as DateLike, '-')
});

const createCancelledAtColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string,
  formatDateTime: FormatDateTimeFn
): GridColDef<T> => ({
  field: invitationColumnFields.cancelledAt,
  headerName: t('trainers.cancelledAt'),
  width: COLUMN_WIDTHS.date,
  sortable: true,
  filterable: true,
  valueFormatter: value => formatDateTime(value as DateLike, '-')
});

const createRejectedAtColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string,
  formatDateTime: FormatDateTimeFn
): GridColDef<T> => ({
  field: invitationColumnFields.rejectedAt,
  headerName: t('trainers.rejectedAt'),
  width: COLUMN_WIDTHS.date,
  sortable: true,
  filterable: true,
  valueFormatter: value => formatDateTime(value as DateLike, '-')
});

const createStatusColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string
): GridColDef<T> => ({
  field: invitationColumnFields.status,
  headerName: t('trainers.status'),
  width: COLUMN_WIDTHS.status,
  sortable: true,
  filterable: true,
  renderCell: params => (
    <InvitationStatusCell
      params={params as GridRenderCellParams<BaseInvitationRow, number>}
      t={t}
    />
  )
});

const isInCourseContext = (row: BaseInvitationRow): boolean => {
  const hasCourseId = row.courseId != null && String(row.courseId).trim() !== '';
  const hasCourseTitle = row.courseTitle != null && String(row.courseTitle).trim() !== '';
  return hasCourseId || hasCourseTitle;
};

const createInCourseContextColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string
): GridColDef<T> => ({
  field: invitationColumnFields.courseId,
  headerName: t('trainers.inCourseContext'),
  width: COLUMN_WIDTHS.inCourseContext,
  sortable: true,
  filterable: true,
  valueGetter: (_value, row) => (isInCourseContext(row) ? t('Yes') : t('No'))
});

const createCourseColumn = <T extends BaseInvitationRow>(
  t: (key: string) => string
): GridColDef<T> => ({
  field: invitationColumnFields.courseTitle,
  headerName: t('trainers.course'),
  width: COLUMN_WIDTHS.course,
  flex: 1,
  sortable: true,
  filterable: true,
  valueGetter: (_value, row) => row.courseTitle ?? '-'
});

const createOrganizationColumn = (t: (key: string) => string): GridColDef<SystemInvitationRow> => ({
  field: invitationColumnFields.organizationName,
  headerName: t('admin.organization'),
  width: COLUMN_WIDTHS.organization,
  flex: 1,
  sortable: true,
  filterable: true,
  valueGetter: (_value, row) => row.organizationName ?? '-'
});

type InvitationActionsCellProps<T extends BaseInvitationRow> = Readonly<{
  params: GridRowParams<T>;
  t: (key: string) => string;
  onCancelInvitation?: (organizationId: string, invitationId: string) => void;
  getCancelParams?: (row: T) => { organizationId: string; invitationId: string };
  isCancelling?: boolean;
}>;

const InvitationActionsCell = <T extends BaseInvitationRow>({
  params,
  t,
  onCancelInvitation,
  getCancelParams,
  isCancelling = false
}: InvitationActionsCellProps<T>) => {
  const invitation = params.row;
  const status = Number(invitation.status);
  const isPending = status === TRAINER_INVITATION_STATUS.PENDING;
  const actions: React.ReactNode[] = [];

  if (isPending && onCancelInvitation != null && getCancelParams != null) {
    const { organizationId, invitationId } = getCancelParams(invitation);
    const label = t('trainers.cancelInvitation');
    actions.push(
      <Tooltip key='cancel-invitation' title={label} placement='bottom'>
        <span style={{ display: 'inline-flex' }}>
          <GridActionsCellItem
            icon={<CancelIcon />}
            label={label}
            onClick={() => onCancelInvitation(organizationId, invitationId)}
            disabled={isCancelling}
          />
        </span>
      </Tooltip>
    );
  }

  return <>{actions}</>;
};

const createActionsColumn = <T extends BaseInvitationRow>(
  params: CreateInvitationColumnsParams<T>
): GridColDef<T> => ({
  field: invitationColumnFields.actions,
  type: 'actions',
  headerName: params.t('admin.actions'),
  width: COLUMN_WIDTHS.actions,
  getActions: getParams => [
    <InvitationActionsCell
      key='actions'
      params={getParams}
      t={params.t}
      onCancelInvitation={params.onCancelInvitation}
      getCancelParams={params.getCancelParams}
      isCancelling={params.isCancelling}
    />
  ]
});

export const createOrganizationInvitationsColumns = (
  params: CreateInvitationColumnsParams<BaseInvitationRow> & { getCancelParams: GetCancelParamsFn }
): GridColDef<BaseInvitationRow>[] => [
  createEmailColumn(params.t),
  createInviterNameColumn(params.t),
  createSentAtColumn(params.t, params.formatDateTime),
  createExpiresAtColumn(params.t, params.formatDateTime),
  createAcceptedAtColumn(params.t, params.formatDateTime),
  createCancelledAtColumn(params.t, params.formatDateTime),
  createRejectedAtColumn(params.t, params.formatDateTime),
  createStatusColumn(params.t),
  createInCourseContextColumn(params.t),
  createCourseColumn(params.t),
  createActionsColumn(params)
];

export const createSystemInvitationsColumns = (
  params: CreateInvitationColumnsParams<SystemInvitationRow> & {
    getCancelParams: GetSystemCancelParamsFn;
  }
): GridColDef<SystemInvitationRow>[] => [
  createOrganizationColumn(params.t),
  createEmailColumn(params.t),
  createInviterNameColumn(params.t),
  createSentAtColumn(params.t, params.formatDateTime),
  createExpiresAtColumn(params.t, params.formatDateTime),
  createAcceptedAtColumn(params.t, params.formatDateTime),
  createCancelledAtColumn(params.t, params.formatDateTime),
  createRejectedAtColumn(params.t, params.formatDateTime),
  createStatusColumn(params.t),
  createInCourseContextColumn(params.t),
  createCourseColumn(params.t),
  createActionsColumn(params)
];
